import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { COMPANY, IMAGES } from "@/data/site";

const lines = ["PRECISION", "MOULDED", "PLASTICS."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#0F172A]"
    >
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={IMAGES.hero}
          alt="Injection moulding machines on the factory floor"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0F172A]/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/30 to-transparent" />
      </motion.div>

      <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "64px 64px" }} />

      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 min-h-screen flex flex-col justify-end pb-16 md:pb-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-[#0055FF]" />
          <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#CBD5E1]">
            {COMPANY.legal}
          </span>
        </motion.div>

        <h1 className="font-display font-900 tracking-tighter leading-[0.88] text-[15vw] sm:text-[13vw] lg:text-[10.5vw] text-white">
          {lines.map((line, i) => (
            <span key={line} className="line-mask">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {i === 2 ? (
                  <span>
                    {line.slice(0, -1)}
                    <span className="text-[#0055FF]">.</span>
                  </span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <p className="max-w-md text-[#CBD5E1] text-base md:text-lg leading-relaxed">
            From thin-wall food packaging to custom job moulding — we turn
            engineering-grade polymers into repeatable, tolerance-held parts for
            every industry.
          </p>
          <a
            href="#quote"
            data-testid="hero-cta"
            className="btn-industrial group inline-flex items-center gap-3 bg-[#0055FF] hover:bg-[#3377FF] text-white px-8 py-4 font-mono-tech text-xs uppercase tracking-widest self-start md:self-auto shrink-0"
          >
            Request a Quote
            <ArrowDown className="h-4 w-4 group-hover:translate-y-1 transition-transform duration-300" />
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
};
